import { djs } from '@duke-hq/djs';
import {
  DOCUMENT_TYPE_LABELS,
  DocumentType,
} from '@duke-hq/svc-api/lib/enrolment/formatPdfDocumentData';
import { calculateEnrolmentMetricsBySemester } from './calculateEnrolmentMetrics';
import { completedUnitOfCompetencyIds } from './completedUnitOfCompetencyIds';
import { CourseSegment, isCourseSegment } from '@duke-hq/shared-constants/enrolments';
import { CourseKind, Course as PrismaCourse, StudentCourseEnrolment } from '@duke-hq/svc-api-db';
import { BasicCourseEnrolmentScheduleItem } from '@duke-hq/svc-api/provider/services/courseService';

type SubsetOfCourse = Pick<PrismaCourse, 'kind'> & {
  accredited?: boolean;
};

// The columns are picked off the model, so a schema change reaches this contract.
export type EnrolmentForDocumentAccess = Pick<
  StudentCourseEnrolment,
  | 'enrolment_contract_type'
  | 'work_schedule'
  | 'start_at_date'
  | 'semester_2_start_at_date'
  | 'course_segment'
  | 'completed_at'
  | 'status'
  | 'created_at'
> & {
  courseEnrolmentScheduleItems: BasicCourseEnrolmentScheduleItem[];
  course?: SubsetOfCourse | null;
};

export interface AvailableDocument {
  documentType: DocumentType;
  documentDisplayName: string;
  documentSemester?: 1 | 2;
}

// -- Completions & progression panel document groupings ----------------------
// The consultant dashboard's "Completions and progression" card splits documents
// into two tiles. Each tile's side panel should only surface the document types
// that belong to it, so the two panels stop showing an identical mixed list.

export const completionDocumentTypes: DocumentType[] = [
  'statementOfAttainment',
  'certificateAndRecordOfResults',
  'nonVocationalCertificate',
];

export const progressionDocumentTypes: DocumentType[] = ['progressReport', 'educationVerification'];

// 'all' is used by the card-level link to show both sets together. The two tiles use
// the specific 'completions' / 'progressions' categories.
export const completionsProgressionsCategories = ['completions', 'progressions', 'all'] as const;
export type CompletionsProgressionsCategory = (typeof completionsProgressionsCategories)[number];

const documentTypesByCategory: Record<CompletionsProgressionsCategory, DocumentType[]> = {
  completions: completionDocumentTypes,
  progressions: progressionDocumentTypes,
  all: [...completionDocumentTypes, ...progressionDocumentTypes],
};

export function filterDocumentsByCategory(
  documents: AvailableDocument[],
  category: CompletionsProgressionsCategory,
): AvailableDocument[] {
  const allowedTypes = documentTypesByCategory[category];
  return documents.filter((doc) => allowedTypes.includes(doc.documentType));
}

function hasCompletedAssessments(enrolment: EnrolmentForDocumentAccess): boolean {
  return (
    completedUnitOfCompetencyIds(
      enrolment.courseEnrolmentScheduleItems,
      enrolment.course?.kind ?? CourseKind.QUALIFICATION,
    ).length > 0
  );
}

function getVOEDocumentForSemester(
  enrolment: EnrolmentForDocumentAccess,
  semester: 1 | 2,
  userUTCOffset: number,
): AvailableDocument[] {
  const metrics = calculateEnrolmentMetricsBySemester(enrolment, semester);
  const hasDueDate = metrics.semesterDueDate?.isoDateOnly != null;
  const isFullyComplete = metrics.semesterProgress >= 100;

  if (!hasDueDate || !isFullyComplete) {
    return [];
  }

  // Convert the release date to the user's timezone (start of day)
  const releaseDateInUserTimezone = djs(metrics.semesterDueDate?.isoDateOnly)
    .utcOffset(userUTCOffset)
    .startOf('day');

  // Get current time in the user's timezone (start of day)
  const nowInUserTimezone = djs().utcOffset(userUTCOffset).startOf('day');

  const isAfterReleaseDate = nowInUserTimezone.isSameOrAfter(releaseDateInUserTimezone, 'day');

  if (!isAfterReleaseDate) {
    return [];
  }

  return [
    {
      documentType: 'educationVerification',
      documentDisplayName: `${DOCUMENT_TYPE_LABELS.educationVerification} - Semester ${semester}`,
      documentSemester: semester,
    },
  ];
}

function getVOEDocuments(
  enrolment: EnrolmentForDocumentAccess,
  userUTCOffset: number,
): AvailableDocument[] {
  const hasIneligibleCourseKind =
    enrolment.course?.kind === CourseKind.SHORT_COURSE ||
    enrolment.course?.kind === CourseKind.NON_VOCATIONAL;
  if (hasIneligibleCourseKind) {
    return [];
  }

  // VOE (Verification of Education) documents are available for IEA contracts
  const isIEAContract = enrolment.enrolment_contract_type === 'IEA';
  if (!isIEAContract) {
    return [];
  }

  const isPartialCourse =
    isCourseSegment(enrolment.course_segment) &&
    enrolment.course_segment !== CourseSegment.FULL_COURSE;

  if (isPartialCourse) {
    return getVOEDocumentForSemester(enrolment, 1, userUTCOffset);
  }

  return [
    ...getVOEDocumentForSemester(enrolment, 1, userUTCOffset),
    ...getVOEDocumentForSemester(enrolment, 2, userUTCOffset),
  ];
}

function getProgressReportDocument(
  enrolment: EnrolmentForDocumentAccess,
  userUTCOffset: number,
): AvailableDocument[] {
  // Progress Report documents are not issued for SHORT_COURSE enrolments
  const isShortCourse = enrolment.course?.kind === CourseKind.SHORT_COURSE;
  if (isShortCourse) {
    return [];
  }

  const isEligibleContractType = ['TTW', 'Workforce Australia', 'IEA'].includes(
    enrolment.enrolment_contract_type,
  );

  const nowInUserTimezone = djs().utcOffset(userUTCOffset).startOf('day');
  const startDateInUserTimezone = djs(enrolment.start_at_date)
    .utcOffset(userUTCOffset)
    .startOf('day');
  const weeksSinceStart = nowInUserTimezone.diff(startDateInUserTimezone, 'week');
  // progress reports should only be released after 26 weeks
  const has26ConsecutiveWeeks = weeksSinceStart >= 26;

  if (!isEligibleContractType || !has26ConsecutiveWeeks) {
    return [];
  }
  return [
    {
      documentType: 'progressReport',
      documentDisplayName: DOCUMENT_TYPE_LABELS.progressReport,
    },
  ];
}

function getCertificateDocument(enrolment: EnrolmentForDocumentAccess): AvailableDocument[] {
  const isCompleted = enrolment.completed_at !== null;
  const isValidSegment =
    isCourseSegment(enrolment.course_segment) &&
    enrolment.course_segment === CourseSegment.FULL_COURSE;
  // For non-vocational and short course enrolments, we don't issue a certificate
  const hasIneligibleCourseKind =
    enrolment.course?.kind === CourseKind.NON_VOCATIONAL ||
    enrolment.course?.kind === CourseKind.SHORT_COURSE;

  if (!isCompleted || !isValidSegment || hasIneligibleCourseKind) {
    return [];
  }

  return [
    {
      documentType: 'certificateAndRecordOfResults',
      documentDisplayName: DOCUMENT_TYPE_LABELS.certificateAndRecordOfResults,
    },
  ];
}

function getStatementOfAttainmentDocument(
  enrolment: EnrolmentForDocumentAccess,
  isAdmin: boolean,
): AvailableDocument[] {
  const isNonVocationalCourse = enrolment.course?.kind === CourseKind.NON_VOCATIONAL;
  if (isNonVocationalCourse) {
    return [];
  }

  const isShortCourse = enrolment.course?.kind === CourseKind.SHORT_COURSE;

  // For SHORT_COURSE enrolments, SOA is only available when the course is fully completed
  if (isShortCourse) {
    const isCompleted = enrolment.completed_at !== null;
    if (isCompleted && hasCompletedAssessments(enrolment)) {
      return [
        {
          documentType: 'statementOfAttainment',
          documentDisplayName: DOCUMENT_TYPE_LABELS.statementOfAttainment,
        },
      ];
    }
    return [];
  }

  // For regular courses:
  // We don't want to issue an SOA if a student has fully completed their course
  // and can also download a certificate. The certificate supersedes the SOA in this case.
  const isCourseFullyCompleted =
    isCourseSegment(enrolment.course_segment) &&
    enrolment.course_segment != CourseSegment.FIRST_SEMESTER_ONLY &&
    enrolment.completed_at !== null;

  if (hasCompletedAssessments(enrolment) && (isAdmin || !isCourseFullyCompleted)) {
    return [
      {
        documentType: 'statementOfAttainment',
        documentDisplayName: DOCUMENT_TYPE_LABELS.statementOfAttainment,
      },
    ];
  }

  return [];
}

function getNonVocationalCertificateDocument(
  enrolment: EnrolmentForDocumentAccess,
): AvailableDocument[] {
  const isCompleted = enrolment.completed_at !== null;

  const isNonVocationalCourse = enrolment.course?.kind === CourseKind.NON_VOCATIONAL;

  if (!isCompleted || !isNonVocationalCourse) {
    return [];
  }

  return [
    {
      documentType: 'nonVocationalCertificate',
      documentDisplayName: DOCUMENT_TYPE_LABELS.certificateAndRecordOfResults,
    },
  ];
}

function getStudentTimesheetDocument(enrolment: EnrolmentForDocumentAccess): AvailableDocument[] {
  const isShortCourse = enrolment.course?.kind === CourseKind.SHORT_COURSE;
  if (isShortCourse) {
    return [];
  }

  if (!enrolment.course?.accredited) {
    return [];
  }

  return [
    {
      documentType: 'studentTimesheet',
      documentDisplayName: DOCUMENT_TYPE_LABELS.studentTimesheet,
    },
  ];
}

function getConfirmationOfEnrolmentDocument(isAdmin: boolean): AvailableDocument[] {
  if (!isAdmin) {
    return [];
  }
  return [
    {
      documentType: 'confirmationOfEnrolment',
      documentDisplayName: DOCUMENT_TYPE_LABELS.confirmationOfEnrolment,
    },
  ];
}

export function listAvailableEnrolmentDocuments({
  enrolment,
  isAdmin = false,
  isStudent = false,
  userUTCOffset,
}: {
  enrolment: EnrolmentForDocumentAccess;
  isAdmin?: boolean;
  isStudent?: boolean;
  userUTCOffset: number;
}): AvailableDocument[] {
  if (enrolment.status === 'WITHDRAWN') {
    return getStatementOfAttainmentDocument(enrolment, isAdmin);
  }

  if (isStudent) {
    return [
      ...getCertificateDocument(enrolment),
      ...getStatementOfAttainmentDocument(enrolment, isAdmin),
      ...getNonVocationalCertificateDocument(enrolment),
    ];
  }

  return [
    ...getVOEDocuments(enrolment, userUTCOffset),
    ...getProgressReportDocument(enrolment, userUTCOffset),
    ...getCertificateDocument(enrolment),
    ...getStatementOfAttainmentDocument(enrolment, isAdmin),
    ...getNonVocationalCertificateDocument(enrolment),
    ...getConfirmationOfEnrolmentDocument(isAdmin),
    ...getStudentTimesheetDocument(enrolment),
  ];
}
